import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { queryClientAtom } from 'jotai-tanstack-query';
import { createContext } from 'react';

import { postVerifyInvitationCodeApi } from '@flipster/apis/v1/copy-trade';
import { i18nBunja } from '@flipster/i18n';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';

import { UserProfileQueryKey } from '../../../profile-page/bunja';

export const InvitationCodePortfolioIdContext = createContext<string>(null!);
export const InvitationCodePortfolioIdScope = createScopeFromContext(InvitationCodePortfolioIdContext);

const invitationAcceptDelay = 1000;

export const invitationCodeModalBunja = bunja(() => {
  const { getT } = bunja.use(i18nBunja);
  const t = getT();

  const store = bunja.use(JotaiStoreScope);
  const portfolioId = bunja.use(InvitationCodePortfolioIdScope);

  const invitationCodeAtom = atom('');
  const errorMessageAtom = atom('');
  const isVerifiedAtom = atom(false);
  const isSubmittingAtom = atom(false);

  const isDisabledAtom = atom((get) => {
    return !get(invitationCodeAtom) || Boolean(get(errorMessageAtom)) || get(isSubmittingAtom);
  });

  async function verifyInvitationCode() {
    const invitationCode = store.get(invitationCodeAtom);
    const isDisabled = store.get(isDisabledAtom);
    if (isDisabled) return { success: false };

    store.set(isSubmittingAtom, true);
    try {
      await postVerifyInvitationCodeApi(portfolioId, { invitationCode });
      store.set(isVerifiedAtom, true);

      const queryClient = store.get(queryClientAtom);
      queryClient.invalidateQueries({ queryKey: ['getCopyTradePortfoliosApi'] });
      queryClient.removeQueries({ queryKey: ['getCopyTradePortfoliosApi'], type: 'inactive' });
      queryClient.invalidateQueries({ queryKey: UserProfileQueryKey });
      queryClient.removeQueries({ queryKey: UserProfileQueryKey, type: 'inactive' });

      await new Promise((resolve) => setTimeout(resolve, invitationAcceptDelay));
      return { success: true };
    } catch (error) {
      store.set(
        errorMessageAtom,
        getErrorCode(error) == 'api.tooManyRequests'
          ? t('Copy.r260306.Too_many_attempts_error')
          : getErrorMessage(error),
      );
      return { success: false };
    } finally {
      store.set(isSubmittingAtom, false);
    }
  }

  return {
    invitationCodeAtom,
    errorMessageAtom,
    isVerifiedAtom,
    isSubmittingAtom,
    isDisabledAtom,
    verifyInvitationCode,
  };
});
invitationCodeModalBunja.debugLabel = 'invitationCodeModalBunja';
